import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Button,
  Stack,
  Collapse,
  IconButton,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

const INHERITANCE_MODES = [
  { value: 1, label: 'Autosomal Dominant' },
  { value: 2, label: 'Autosomal Recessive' },
  { value: 3, label: 'Digenic Recessive' },
  { value: 4, label: 'Digenic Dominant' },
  { value: 5, label: 'Somatic' },
  { value: 6, label: 'Multifactorial' },
  { value: 7, label: 'X-Linked' },
  { value: 8, label: 'X-Linked Dominant' },
  { value: 9, label: 'X-Linked Recessive' },
  { value: 10, label: 'Y-Linked' },
  { value: 11, label: 'Unknown' },
]

const InheritanceFilter = ({ selectedModes = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(true)

  const handleChange = (event) => {
    const value = parseInt(event.target.value, 10)
    const newSelectedModes = event.target.checked
      ? [...selectedModes, value]
      : selectedModes.filter((mode) => mode !== value)
    onChange(newSelectedModes)
  }

  const handleSelectAll = () => {
    onChange(INHERITANCE_MODES.map((mode) => mode.value))
  }

  const handleClearAll = () => {
    onChange([])
  }

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          mb: 1.5,
          cursor: 'pointer',
          '&:hover': {
            '& .MuiTypography-root': {
              color: 'text.primary',
            },
          },
        }}
        onClick={toggleOpen}
      >
        <IconButton size="small" sx={{ mr: 1, p: 0 }}>
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography variant="subtitle2" color="textSecondary" sx={{ flex: 1 }}>
          Inheritance Mode {selectedModes.length > 0 && `(${selectedModes.length})`}
        </Typography>
        <Box sx={{ ml: 1 }}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleSelectAll()
            }}
            sx={{
              minWidth: 'auto',
              mr: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleClearAll()
            }}
            sx={{
              minWidth: 'auto',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'none',
              color: 'text.secondary',
            }}
          >
            Clear
          </Button>
        </Box>
      </Stack>
      <Collapse in={isOpen}>
        <FormGroup
          sx={{
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '& .MuiFormControlLabel-root': {
              mb: 0.75,
              ml: 0,
              mr: 0,
              '&:last-child': {
                mb: 0,
              },
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '0.9rem',
              color: 'text.primary',
            },
            '& .MuiCheckbox-root': {
              p: 0.75,
              color: 'primary.main',
              '&.Mui-checked': {
                color: 'primary.main',
              },
            },
          }}
        >
          {INHERITANCE_MODES.map(({ value, label }) => (
            <FormControlLabel
              key={value}
              control={
                <Checkbox checked={selectedModes.includes(value)} onChange={handleChange} value={value} size="small" />
              }
              label={label}
            />
          ))}
        </FormGroup>
      </Collapse>
    </Box>
  )
}

InheritanceFilter.propTypes = {
  selectedModes: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
}

export default InheritanceFilter
